/**
 * Territory Repository
 * Handles district and region operations with authority information
 */

import { supabase } from '@/supabase.config';
import { District } from '@/models/district';
import { Region } from '@/models/region';
import { Minister } from '@/models/minister';
import type { AuthorityPosition, AuthorityRole, OrgType } from '@/models/authorityPosition';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/**
 * Get all active districts with superintendent info and active minister count.
 * Used by the Directorio Nacional information tab.
 */
export async function getDistrictsWithStats(): Promise<(District & { activeMemberCount: number })[]> {
  try {
    // Two focused queries are faster than one combined query with multiple joins + aggregate
    const [districtsResult, countsResult] = await Promise.all([
      supabase
        .from('districts')
        .select(`
          id, name, code, is_active,
          superintendent:ministers!superintendent_id(id, adb_code, full_name, profile_picture_filename),
          vice_superintendent:ministers!vice_superintendent_id(id, adb_code, full_name, profile_picture_filename)
        `)
        .eq('is_active', true)
        .order('name', { ascending: true }),
      supabase
        .from('ministers')
        .select('district_id')
        .eq('is_active', true)
        .not('district_id', 'is', null),
    ]);

    if (districtsResult.error) throw districtsResult.error;
    if (countsResult.error) throw countsResult.error;

    // Count active ministers per district in JS
    const countMap = new Map<string, number>();
    for (const m of countsResult.data || []) {
      if (m.district_id) {
        countMap.set(m.district_id, (countMap.get(m.district_id) ?? 0) + 1);
      }
    }

    return ((districtsResult.data || []) as District[]).map((d) => ({
      ...d,
      activeMemberCount: countMap.get(d.id) ?? 0,
    }));
  } catch (error) {
    console.error('Error fetching districts with stats:', error);
    return [];
  }
}

/**
 * Get all districts with their regions and authorities
 */
export async function getAllDistrictsWithAuthorities(): Promise<District[]> {
  try {
    const { data, error } = await supabase
      .from('districts')
      .select(`
        *,
        superintendent:ministers!superintendent_id(id, adb_code, full_name, profile_picture_filename),
        vice_superintendent:ministers!vice_superintendent_id(id, adb_code, full_name, profile_picture_filename),
        secretary:ministers!secretary_id(id, adb_code, full_name, profile_picture_filename),
        treasurer:ministers!treasurer_id(id, adb_code, full_name, profile_picture_filename),
        vocal1:ministers!vocal1_id(id, adb_code, full_name, profile_picture_filename),
        vocal2:ministers!vocal2_id(id, adb_code, full_name, profile_picture_filename)
      `)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []) as District[];
  } catch (error) {
    console.error('Error fetching districts with authorities:', error);
    return [];
  }
}

/**
 * Get a single district by ID with authorities and regions
 */
export async function getDistrictById(id: string): Promise<District | null> {
  try {
    const { data, error } = await supabase
      .from('districts')
      .select(`
        *,
        superintendent:ministers!superintendent_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vice_superintendent:ministers!vice_superintendent_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        secretary:ministers!secretary_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        treasurer:ministers!treasurer_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal1:ministers!vocal1_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal2:ministers!vocal2_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as District;
  } catch (error) {
    console.error('Error fetching district by ID:', error);
    return null;
  }
}

/**
 * Get all regions for a district with authorities
 */
export async function getRegionsByDistrictId(districtId: string): Promise<Region[]> {
  try {
    const { data, error } = await supabase
      .from('regions')
      .select(`
        *,
        presbyter:ministers!presbyter_id(id, adb_code, full_name, profile_picture_filename),
        vice_presbyter:ministers!vice_presbyter_id(id, adb_code, full_name, profile_picture_filename)
      `)
      .eq('district_id', districtId)
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) throw error;
    return (data || []) as Region[];
  } catch (error) {
    console.error('Error fetching regions by district:', error);
    return [];
  }
}

/**
 * Get regions by district with full authority information and active minister count
 */
export async function getRegionsWithStatsForDistrict(districtId: string): Promise<(Region & { activeMemberCount: number })[]> {
  try {
    // Get regions with authorities using a single query
    const { data: regions, error: regionsError } = await supabase
      .from('regions')
      .select(`
        *,
        presbyter:ministers!presbyter_id(id, adb_code, full_name, profile_picture_filename),
        vice_presbyter:ministers!vice_presbyter_id(id, adb_code, full_name, profile_picture_filename),
        ministers!region_id(count)
      `)
      .eq('district_id', districtId)
      .eq('is_active', true)
      .eq('ministers.is_active', true)
      .order('name', { ascending: true });

    if (regionsError) throw regionsError;

    // Transform the data to include activeMemberCount from the aggregated count
    interface RegionWithCount extends Region {
      ministers?: Array<{ count?: number }>;
    }
    
    const typedRegions = (regions || []) as RegionWithCount[];
    return typedRegions.map(region => {
      // Extract the count from the ministers aggregation
      const ministerCount = Array.isArray(region.ministers) && region.ministers.length > 0 
        ? region.ministers[0].count || 0 
        : 0;
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { ministers: _ministers, ...regionData } = region;
      
      return {
        ...regionData,
        activeMemberCount: ministerCount
      } as Region & { activeMemberCount: number };
    });
  } catch (error) {
    console.error('Error fetching regions with stats:', error);
    return [];
  }
}

/**
 * Get a single region by ID with authorities
 */
export async function getRegionById(id: string): Promise<Region | null> {
  try {
    const { data, error } = await supabase
      .from('regions')
      .select(`
        *,
        district:districts(id, name, code),
        presbyter:ministers!presbyter_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vice_presbyter:ministers!vice_presbyter_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        secretary:ministers!secretary_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        treasurer:ministers!treasurer_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal1:ministers!vocal1_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal2:ministers!vocal2_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Region;
  } catch (error) {
    console.error('Error fetching region by ID:', error);
    return null;
  }
}

// Maps district FK column names to authority_positions role values
const DISTRICT_ROLE_MAP: Record<string, string> = {
  superintendent_id:      'SUPERINTENDENT',
  vice_superintendent_id: 'VICE_SUPERINTENDENT',
  secretary_id:           'SECRETARY',
  treasurer_id:           'TREASURER',
  vocal1_id:              'VOCAL_1',
  vocal2_id:              'VOCAL_2',
};

// Maps region FK column names to authority_positions role values
const REGION_ROLE_MAP: Record<string, string> = {
  presbyter_id:      'PRESBYTER',
  vice_presbyter_id: 'VICE_PRESBYTER',
  secretary_id:      'SECRETARY',
  treasurer_id:      'TREASURER',
  vocal1_id:         'VOCAL_1',
  vocal2_id:         'VOCAL_2',
};

/**
 * Syncs authority_positions history records when district/region officers change.
 * - If ministerId is non-null: closes any active record for that role, then opens a new one.
 * - If ministerId is null: closes the active record (position vacated).
 */
async function syncAuthorityPosition(opts: {
  orgType: 'DISTRICT' | 'REGION';
  orgId: string;
  role: string;
  ministerId: string | null;
  startedAt: string;  // ISO date string, e.g. "2026-04-10"
}): Promise<void> {
  const { orgType, orgId, role, ministerId, startedAt } = opts;
  const orgFilter = orgType === 'DISTRICT' ? { district_id: orgId } : { region_id: orgId };

  // Close any currently active record for this role
  const { error: closeError } = await db
    .from('authority_positions')
    .update({ ended_at: startedAt, updated_at: new Date().toISOString() })
    .match({ ...orgFilter, role })
    .is('ended_at', null);

  if (closeError) throw closeError;

  // Open a new record if a minister was assigned
  if (ministerId) {
    const { error: insertError } = await db
      .from('authority_positions')
      .insert({
        org_type:    orgType,
        ...(orgType === 'DISTRICT' ? { district_id: orgId } : { region_id: orgId }),
        minister_id: ministerId,
        role,
        started_at:  startedAt,
      });

    if (insertError) throw insertError;
  }
}

/**
 * Update district authorities
 */
export async function updateDistrictAuthorities(
  districtId: string,
  authorities: {
    superintendent_id?: string | null;
    vice_superintendent_id?: string | null;
    secretary_id?: string | null;
    treasurer_id?: string | null;
    vocal1_id?: string | null;
    vocal2_id?: string | null;
  },
  startedAt?: string
): Promise<District | null> {
  try {
    const updateData: Record<string, string | null> = {
      ...authorities,
      updated_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from('districts')
      .update(updateData as unknown as never)
      .eq('id', districtId);

    if (error) throw error;

    // Sync authority_positions history for each changed role
    const effectiveDate = startedAt ?? new Date().toISOString().slice(0, 10);
    await Promise.all(
      Object.entries(authorities).map(([col, ministerId]) => {
        const role = DISTRICT_ROLE_MAP[col];
        if (!role) return Promise.resolve();
        return syncAuthorityPosition({
          orgType: 'DISTRICT',
          orgId: districtId,
          role,
          ministerId: ministerId ?? null,
          startedAt: effectiveDate,
        });
      })
    );

    // Fetch the updated district
    const { data, error: fetchError } = await supabase
      .from('districts')
      .select(`
        *,
        superintendent:ministers!superintendent_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vice_superintendent:ministers!vice_superintendent_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        secretary:ministers!secretary_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        treasurer:ministers!treasurer_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal1:ministers!vocal1_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal2:ministers!vocal2_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id)
      `)
      .eq('id', districtId)
      .single();

    if (fetchError) throw fetchError;

    return data as District;
  } catch (error) {
    console.error('Error updating district authorities:', error);
    throw error;
  }
}

/**
 * Update region authorities
 */
export async function updateRegionAuthorities(
  regionId: string,
  authorities: {
    presbyter_id?: string | null;
    vice_presbyter_id?: string | null;
    secretary_id?: string | null;
    treasurer_id?: string | null;
    vocal1_id?: string | null;
    vocal2_id?: string | null;
  },
  startedAt?: string
): Promise<Region | null> {
  try {
    const updateData: Record<string, string | null> = {
      ...authorities,
      updated_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from('regions')
      .update(updateData as unknown as never)
      .eq('id', regionId);

    if (error) throw error;

    // Sync authority_positions history for each changed role
    const effectiveDate = startedAt ?? new Date().toISOString().slice(0, 10);
    await Promise.all(
      Object.entries(authorities).map(([col, ministerId]) => {
        const role = REGION_ROLE_MAP[col];
        if (!role) return Promise.resolve();
        return syncAuthorityPosition({
          orgType: 'REGION',
          orgId: regionId,
          role,
          ministerId: ministerId ?? null,
          startedAt: effectiveDate,
        });
      })
    );

    // Fetch the updated region
    const { data, error: fetchError } = await supabase
      .from('regions')
      .select(`
        *,
        district:districts(id, name, code),
        presbyter:ministers!presbyter_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vice_presbyter:ministers!vice_presbyter_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        secretary:ministers!secretary_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        treasurer:ministers!treasurer_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal1:ministers!vocal1_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id),
        vocal2:ministers!vocal2_id(id, adb_code, full_name, profile_picture_filename, district_id, region_id)
      `)
      .eq('id', regionId)
      .single();

    if (fetchError) throw fetchError;

    return data as Region;
  } catch (error) {
    console.error('Error updating region authorities:', error);
    throw error;
  }
}

/**
 * Get all assigned minister IDs for a district and its regions
 * Used to prevent assigning the same minister to multiple positions
 */
export async function getAssignedMinisterIds(districtId: string): Promise<string[]> {
  try {
    const assignedIds = new Set<string>();

    // Get district authorities
    const { data: district, error: districtError } = await supabase
      .from('districts')
      .select('superintendent_id, vice_superintendent_id, secretary_id, treasurer_id, vocal1_id, vocal2_id')
      .eq('id', districtId)
      .single();

    if (districtError) throw districtError;

    if (district) {
      interface DistrictAuthorities {
        superintendent_id?: string | null;
        vice_superintendent_id?: string | null;
        secretary_id?: string | null;
        treasurer_id?: string | null;
        vocal1_id?: string | null;
        vocal2_id?: string | null;
      }
      const districtData = district as DistrictAuthorities;
      [districtData.superintendent_id, districtData.vice_superintendent_id, 
       districtData.secretary_id, districtData.treasurer_id, 
       districtData.vocal1_id, districtData.vocal2_id
      ].forEach(id => id && assignedIds.add(id));
    }

    // Get all regions' authorities in the district
    const { data: regions, error: regionsError } = await supabase
      .from('regions')
      .select('presbyter_id, vice_presbyter_id, secretary_id, treasurer_id, vocal1_id, vocal2_id')
      .eq('district_id', districtId);

    if (regionsError) throw regionsError;

    if (regions) {
      interface RegionAuthorities {
        presbyter_id?: string | null;
        vice_presbyter_id?: string | null;
        secretary_id?: string | null;
        treasurer_id?: string | null;
        vocal1_id?: string | null;
        vocal2_id?: string | null;
      }
      const typedRegions = regions as RegionAuthorities[];
      typedRegions.forEach(region => {
        [region.presbyter_id, region.vice_presbyter_id,
         region.secretary_id, region.treasurer_id,
         region.vocal1_id, region.vocal2_id
        ].forEach(id => id && assignedIds.add(id));
      });
    }

    return Array.from(assignedIds);
  } catch (error) {
    console.error('Error fetching assigned minister IDs:', error);
    return [];
  }
}

/**
 * Get all active ministers for authority assignment (with search)
 * Supports partial matching on adb_code and full_name
 * Can be scoped to a specific district or region
 * Uses AND logic for multiple search terms (like ministers grid)
 */
export async function searchMinistersForAuthority(
  query: string,
  districtId?: string | null,
  regionId?: string | null,
  limit: number = 50
): Promise<Minister[]> {
  try {
    let queryBuilder = supabase
      .from('ministers')
      .select('id, adb_code, full_name, district_id, region_id')
      .eq('is_active', true);
    
    // Scope by district or region if provided
    if (districtId) {
      queryBuilder = queryBuilder.eq('district_id', districtId);
    }
    if (regionId) {
      queryBuilder = queryBuilder.eq('region_id', regionId);
    }
    
    // If query provided, use AND logic for multiple terms (like ministers grid)
    if (query.trim().length > 0) {
      const searchTokens = query.trim().split(/\s+/).filter(token => token.length > 0);
      
      if (searchTokens.length === 1) {
        // Single token: OR search across code and name fields
        const token = searchTokens[0];
        queryBuilder = queryBuilder.or(
          `adb_code.ilike.%${token}%,` +
          `full_name.ilike.%${token}%`
        );
      } else {
        // Multiple tokens: each must appear in full_name (AND logic via chained ilike)
        searchTokens.forEach(token => {
          queryBuilder = queryBuilder.ilike('full_name', `%${token}%`);
        });
      }
    }
    
    const { data, error } = await queryBuilder
      .limit(limit)
      .order('full_name', { ascending: true });

    if (error) throw error;
    return (data || []) as Minister[];
  } catch (error) {
    console.error('Error searching ministers:', error);
    return [];
  }
}

/**
 * Get districts and regions tree structure for sidebar
 * Optimized to fetch everything in a single query
 */
export async function getDistrictsWithRegionsTree(): Promise<District[]> {
  try {
    const { data, error } = await supabase
      .from('districts')
      .select(`
        id,
        name,
        code,
        is_active,
        regions!district_id(
          id,
          name,
          code,
          district_id,
          is_active
        )
      `)
      .eq('is_active', true)
      .eq('regions.is_active', true)
      .order('name', { ascending: true })
      .order('name', { foreignTable: 'regions', ascending: true });

    if (error) throw error;
    
    // Transform the data structure to match District type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const districts = (data || []).map((district: any) => ({
      ...district,
      // Ensure regions is always an array (Supabase returns array from join)
      regions: Array.isArray(district.regions) ? district.regions : [],
    })) as District[];

    return districts;
  } catch (error) {
    console.error('Error fetching districts with regions tree:', error);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Authority Positions (position history)
// ---------------------------------------------------------------------------

/**
 * Fetch all authority_positions records for a district or region,
 * joined with basic minister info, ordered by role then started_at desc.
 */
export async function getAuthorityPositions(opts: {
  districtId?: string;
  regionId?: string;
}): Promise<AuthorityPosition[]> {
  try {
    let query = db
      .from('authority_positions')
      .select(`
        *,
        minister:ministers!minister_id(id, adb_code, full_name, profile_picture_filename)
      `)
      .order('role', { ascending: true })
      .order('started_at', { ascending: false });

    if (opts.districtId) query = query.eq('district_id', opts.districtId);
    if (opts.regionId)   query = query.eq('region_id', opts.regionId);

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as AuthorityPosition[];
  } catch (error) {
    console.error('Error fetching authority positions:', error);
    return [];
  }
}

/**
 * Update an existing authority_positions record (dates and/or notes).
 * If updating an active record's started_at, also updates the FK column
 * on the parent district/region is NOT needed (minister hasn't changed).
 */
export async function updateAuthorityPosition(
  id: string,
  updates: { started_at?: string; ended_at?: string | null; notes?: string | null }
): Promise<void> {
  const { error } = await db
    .from('authority_positions')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/**
 * End an active authority position (set ended_at = today) and clear
 * the corresponding FK column on the district or region.
 */
export async function endAuthorityPosition(
  position: Pick<AuthorityPosition, 'id' | 'org_type' | 'district_id' | 'region_id' | 'role'>
): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);

  const { error } = await db
    .from('authority_positions')
    .update({ ended_at: today, updated_at: new Date().toISOString() })
    .eq('id', position.id);
  if (error) throw error;

  // Clear the FK column so the InformationTab reflects the vacancy
  const roleToCol: Record<string, string> = {
    SUPERINTENDENT:      'superintendent_id',
    VICE_SUPERINTENDENT: 'vice_superintendent_id',
    PRESBYTER:           'presbyter_id',
    VICE_PRESBYTER:      'vice_presbyter_id',
    SECRETARY:           'secretary_id',
    TREASURER:           'treasurer_id',
    VOCAL_1:             'vocal1_id',
    VOCAL_2:             'vocal2_id',
  };
  const col = roleToCol[position.role];
  if (!col) return;

  if (position.org_type === 'DISTRICT' && position.district_id) {
    const { error: fkErr } = await supabase
      .from('districts')
      .update({ [col]: null, updated_at: new Date().toISOString() } as unknown as never)
      .eq('id', position.district_id);
    if (fkErr) throw fkErr;
  } else if (position.org_type === 'REGION' && position.region_id) {
    const { error: fkErr } = await supabase
      .from('regions')
      .update({ [col]: null, updated_at: new Date().toISOString() } as unknown as never)
      .eq('id', position.region_id);
    if (fkErr) throw fkErr;
  }
}

// ---------------------------------------------------------------------------
// National authority positions (PRESIDENT, VICE_PRESIDENT, SECRETARY, TREASURER)
// No FK columns to sync — authority_positions IS the source of truth here.
// ---------------------------------------------------------------------------

/**
 * Fetch all authority_positions records for NATIONAL scope,
 * joined with basic minister info, ordered by role then started_at desc.
 */
export async function getNationalAuthorityPositions(): Promise<AuthorityPosition[]> {
  try {
    const { data, error } = await db
      .from('authority_positions')
      .select(`
        *,
        minister:ministers!minister_id(id, adb_code, full_name, profile_picture_filename)
      `)
      .eq('org_type', 'NATIONAL')
      .order('role', { ascending: true })
      .order('started_at', { ascending: false });

    if (error) throw error;
    return (data || []) as AuthorityPosition[];
  } catch (error) {
    console.error('Error fetching national authority positions:', error);
    return [];
  }
}

/**
 * Assign (or vacate) a national authority role.
 * Closes any currently active record for that role, then opens a new one
 * if ministerId is non-null. No FK columns need updating.
 */
export async function updateNationalAuthority(
  role: AuthorityRole,
  ministerId: string | null,
  startedAt: string
): Promise<void> {
  // Close active record for this role
  const { error: closeError } = await db
    .from('authority_positions')
    .update({ ended_at: startedAt, updated_at: new Date().toISOString() })
    .match({ org_type: 'NATIONAL', role })
    .is('ended_at', null);

  if (closeError) throw closeError;

  if (ministerId) {
    const { error: insertError } = await db
      .from('authority_positions')
      .insert({
        org_type:    'NATIONAL',
        district_id: null,
        region_id:   null,
        minister_id: ministerId,
        role,
        started_at:  startedAt,
      });

    if (insertError) throw insertError;
  }
}

/**
 * Insert a historical authority_positions record (ended_at is required —
 * new active records must go through updateDistrictAuthorities /
 * updateRegionAuthorities to keep FK columns in sync).
 */
export async function addHistoricalAuthorityPosition(record: {
  orgType: OrgType;
  districtId?: string;
  regionId?: string;
  ministerId: string;
  role: AuthorityRole;
  startedAt: string;
  endedAt: string;
  notes?: string;
}): Promise<void> {
  const { error } = await db.from('authority_positions').insert({
    org_type:    record.orgType,
    district_id: record.districtId ?? null,
    region_id:   record.regionId ?? null,
    minister_id: record.ministerId,
    role:        record.role,
    started_at:  record.startedAt,
    ended_at:    record.endedAt,
    notes:       record.notes ?? null,
  });
  if (error) throw error;
}
